import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip } from 'recharts';
import { useAuth } from '../hooks/AuthContext';
import useReport from '../hooks/useReport';

// SectionHeader component
const SectionHeader = ({ title }) => (
  <h2 className="text-3xl font-bold mb-6">{title}</h2>
);

const ReportPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { packageDetails, assessments } = location.state || {};
  const { authData } = useAuth();
  const access_token = authData?.access_token || authData?.accessToken;

  // Import useReport hook
  const { fetchReport } = useReport();
  
  // State for loading and error handling
  const [loading, setLoading] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [error, setError] = useState(null);
  const [packageEvalData, setPackageEvalData] = useState(null);

  // Fetch package evaluation data
  useEffect(() => {
    const fetchPackageEvalData = async () => {
      if (!packageDetails?._id) return;

      setLoading(true);
      try {
        const response = await fetch(`https://app.spiralreports.com/api/package-eval/${packageDetails._id}`, {
          headers: {
            'Authorization': `Bearer ${access_token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch package evaluation data');
        }

        const data = await response.json();
        setPackageEvalData(data.data[0]);
      } catch (error) {
        console.error('Error fetching package evaluation data:', error);
        setError('Failed to fetch evaluation data');
      } finally {
        setLoading(false);
      }
    };

    fetchPackageEvalData();
  }, [packageDetails?._id, access_token]);

  // Industry comparison data
  const barChartData = [
    { name: "HP's Cyber Risk Report (2014)", value: 70 },
    { name: 'NTT Global Threat Report (2015)', value: 76 },
    { name: 'Software Engineering Institute (2015)', value: 90 },
    { name: 'Strategic Security Survey (2015)', value: 97 },
    { name: 'Verizon Data Breach Investigations Report (2015)', value: 99.9 }
  ];

  // Conclusion text
  const conclusion = "Our assessment provides a comprehensive view of your organization's compliance maturity. Take action now to strengthen your security posture and meet industry standards.";

  // Circular progress component
  const CircularProgress = ({ percentage, size = 'lg' }) => {
    const radius = size === 'lg' ? 35 : 25;
    const circumference = 2 * Math.PI * radius;
    const progress = ((100 - percentage) / 100) * circumference;
    const displayPercentage = Math.round(percentage);

    return (
      <div className="relative inline-flex">
        <svg className={size === 'lg' ? 'w-32 h-32' : 'w-24 h-24'}>
          <circle
            className="text-gray-200"
            strokeWidth="5"
            stroke="currentColor"
            fill="transparent"
            r={radius}
            cx={size === 'lg' ? '64' : '48'}
            cy={size === 'lg' ? '64' : '48'}
          />
          <circle
            className="text-red-500 -rotate-90 transform origin-center transition-all duration-1000 ease-out"
            strokeWidth="5"
            strokeDasharray={circumference}
            strokeDashoffset={progress}
            strokeLinecap="round"
            stroke="currentColor"
            fill="transparent"
            r={radius}
            cx={size === 'lg' ? '64' : '48'}
            cy={size === 'lg' ? '64' : '48'}
          />
        </svg>
        <span className={`absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 
          ${size === 'lg' ? 'text-xl' : 'text-base'} font-medium`}>
          {displayPercentage}%
        </span>
      </div>
    );
  };

  // Custom tooltip for the bar chart
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-4 shadow-lg rounded-lg border border-gray-200">
          <p className="text-sm font-medium text-gray-900">{label}</p>
          <p className="text-sm text-red-600">
            Score: {payload[0].value.toFixed(1)}%
          </p>
        </div>
      );
    }
    return null;
  };

  // Handle View Report button click
  const handleViewReport = async (assessment) => {
    if (reportLoading) return;

    setReportLoading(true);
    setError(null);
    
    try {
      // Find the matching evaluation from packageEvalData
      const matchingEvaluation = packageEvalData?.evaluations?.find(
        evaluation => evaluation.assessmentId === assessment._id
      );

      if (!matchingEvaluation) {
        throw new Error('Evaluation not found');
      }

      // Fetch evaluation details using the evaluation ID
      const response = await fetch(
        `https://app.spiralreports.com/api/evaluations/${matchingEvaluation._id}`,
        {
          headers: {
            'Authorization': `Bearer ${access_token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch evaluation details');
      }

      const evaluationData = await response.json();

      // Navigate to report page with evaluation data
      navigate(`/report/${assessment._id}`, { 
        state: { 
          reportData: evaluationData.data,
          packageDetails: {
            _id: packageDetails._id,
            name: packageDetails.name,
            compliance: packageDetails.compliance,
            status: packageDetails.status,
            createdAt: packageDetails.createdAt
          },
          assessmentData: assessment
        } 
      });
    } catch (error) {
      console.error('Error fetching report:', error);
      setError('Failed to fetch report data. Please try again.');
    } finally {
      setReportLoading(false);
    }
  };

  // Calculate the overall maturity
  const calculateOverallMaturity = () => {
    if (!assessments?.length) return 0;
    
    const totalCredReq = assessments.reduce((sum, assessment) => sum + (assessment.credReq || 0), 0);
    const maxPossibleScore = assessments.length * 5; // Assuming max credReq is 5
    const maturityPercentage = (totalCredReq / maxPossibleScore) * 100;
    
    return Math.round(maturityPercentage);
  };

  // If no data is available, show error state
  if (!packageDetails || !assessments) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="text-xl text-gray-600 mb-4">No report data available</div>
          <button
            onClick={() => navigate('/dashboard')}
            className="bg-red-500 text-white px-6 py-2 rounded-lg hover:bg-red-600 transition-colors"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header Section */}
      <div className="bg-gray-800 p-8">
        <div className="max-w-6xl mx-auto text-white">
          {/* Title and Description */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold mb-4">
              {packageDetails.name} - Maturity Assessment
            </h1>
            <p className="text-gray-300">
              Overall Readiness: {calculateOverallMaturity()}%
            </p>
            <p className="text-gray-300 mt-2">
              {packageDetails.description || 'Technical assessment report for compliance and security maturity evaluation'}
            </p>
          </div>

          {/* Chart Section */}
          <div className="bg-gray-900 rounded-xl p-6">
            <h2 className="text-xl font-semibold mb-6">Industry Comparison</h2>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={barChartData} layout="vertical" margin={{ left: 20 }}>
                <XAxis 
                  type="number" 
                  stroke="#fff"
                  domain={[0, 100]}
                  tickFormatter={(value) => `${value}%`}
                />
                <YAxis 
                  dataKey="name" 
                  type="category" 
                  stroke="#fff" 
                  width={150}
                  tick={{ fill: '#fff', fontSize: 12 }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar 
                  dataKey="value" 
                  fill="#38bdf8"
                  radius={[0, 4, 4, 0]}
                  animationDuration={2000}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Detailed Reports Section */}
      <div className="max-w-6xl mx-auto px-8 py-12">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h2 className="text-2xl font-bold">Assessment Details</h2>
            <p className="text-gray-600 mt-1">
              Framework: {packageDetails.compliance}
            </p>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-600">
              Status: {packageDetails.status}
            </span>
            <span className="text-sm text-gray-600">
              Created: {new Date(packageDetails.createdAt).toLocaleDateString()}
            </span>
          </div>
        </div>

        {/* Assessment Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {assessments.map((assessment) => (
            <div 
              key={assessment._id} 
              className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300"
            >
              <div className="p-6 flex flex-col items-center">
                <CircularProgress percentage={(assessment.credReq / 5) * 100} />
                <h3 className="text-xl font-semibold mt-4 mb-2 text-center">
                  {assessment.title}
                </h3>
                <p className="text-gray-600 text-center mb-4">
                  {assessment.description}
                </p>
                {assessment.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-4 justify-center">
                    {assessment.tags.map((tag, index) => (
                      <span 
                        key={index}
                        className="bg-gray-100 text-gray-600 px-3 py-1 rounded-full text-sm"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
                <div className="w-full mt-auto">
                  <button 
                    onClick={() => handleViewReport(assessment)}
                    disabled={reportLoading}
                    className={`w-full bg-red-500 text-white py-2 px-4 rounded-lg transition-colors ${
                      reportLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-red-600'
                    }`}
                  >
                    {reportLoading ? 'Loading...' : 'View Details'}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Conclusion Section */}
      <div className="bg-red-500">
        <div className="max-w-4xl mx-auto px-4 py-16">
          <div className="text-white text-center mb-16">
            <SectionHeader title="Conclusion" />
            <p className="text-lg leading-relaxed max-w-3xl mx-auto">
              {conclusion}
            </p>
          </div>
          <div className="bg-white rounded-xl p-16 text-center shadow-2xl mx-auto max-w-3xl">
            <h3 className="text-gray-900 text-4xl font-bold mb-10">
              Want To Level Up Your Compliance?
            </h3>
            <a 
              href="https://spiralreports.com/contact-us" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              <button className="bg-red-500 text-white px-10 py-5 rounded-full hover:bg-red-600 transition-all duration-200 font-medium text-xl shadow-lg hover:shadow-xl transform hover:-translate-y-1">
                Schedule a call with us
              </button>
            </a>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="fixed bottom-4 right-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white" />
        </div>
      )}
    </div>
  );
};

export default ReportPage;